import type { SupportedLocale } from '@/lib/utils/localization/getFlattenedLocales'
import type { SendRequestType } from '@/lib/utils/shared/httpTools'
import httpTools from '@/lib/utils/shared/httpTools'
import type { OrderRecurrenceType } from '@/store/types/order'
import type { HydraErrorType } from '../_hydraError.d'
import type { OrderModelType } from './orderHelpers/buildOrder'

type Props = {
  locale: SupportedLocale
  order: OrderModelType
  token: string
  pathPart?: 'orders' | 'autoorders'
  timeout?: number
  returnStructuredErrors?: boolean
}

export type HydraPostOrderResponse = {
  market: string
  notes: string
  shippingMethod: {
    type: string
    href: string
    location: string
    class?: string
    service: {
      id: {
        unicity: string
      }
    }
    warehouse: {
      id: {
        unicity: string
      }
    }
  }
  shipToAddress: {
    country: string
    state: string
    city: string
    zip: string
    address1: string
    address2: string
  }
  shipToEmail: string
  shipToName: {
    firstName: string
    lastName: string
  }
  shipToPhone: string
  shipToTime: null | string
  source: {
    agent: string
    campaign: null | string
    medium: string
    platform: string
    referrer: null | string
    version: string
  }
  type: null | string
  customer: {
    enroller: {
      href: string
      id: {
        unicity: string
      }
      humanName: {
        fullName: string
      }
      email?: string
      type?: string
    }
    href: string
    humanName: {
      firstName: string
      lastName: string
      fullName: string
      'fullName@en': string
    }
    id: {
      unicity: string
    }
    sponsor: {
      href: string
      id: {
        unicity: string
      }
      humanName: {
        fullName: string
        firstName?: string
        lastName?: string
      }
      email?: string
      type?: string
    }
    status: string
    taxTerms: {
      exemptions: {
        exemptionId: string
        description: string
        type: string
        jurisdiction: {
          country: string
          state: string
        }
        startDate: string
      }[]
    }
    type: string
  }
  lines: {
    items: {
      quantity: number
      item: {
        href: string
        id: {
          unicity: string
        }
        dimensionsEach: {
          depth: number
          height: number
          unit: string
          width: number
        }
        taxedAs: string
        weightEach: {
          unit: string
          value: number
        }
      }
      terms: {
        cvEach: number
        priceEach: number
        pvEach: number
        subtotal: null | number
        tax: {
          categoryDescription: string
          aggregate: {
            items: {
              jurisdiction: {
                address2?: string
                country?: string
              }
              type: string
              amount: number
              percentage: number
              categoryDescription?: string
              description?: string
              taxedAmount?: number
            }[]
            amount: number
          }
          amount?: number
          percentage?: number
          taxablePriceEach?: number
        }
        taxablePriceEach?: number
        pv?: number
        price?: number
        discount?: {
          amount: number
        }
      }
      quantityLimit: {
        quantityQuota: number
        timeFrameDays: number
        quantityOrdered: number
      }
      catalogSlide: {
        content: {
          description: string
        }
      }
      kitChildren?: {
        item: {
          dimensionsEach: {
            depth: number
            height: number
            unit: string
            width: number
          }
          id: {
            unicity: string
          }
          taxedAs: string
          weightEach: {
            unit: string
            value: number
          }
        }
        quantity: number
        catalogSlide: {
          content: {
            description: string
          }
        }
        terms: {
          cvEach: number
          priceEach: number
          pvEach: number
          subtotal: null | number
          tax: {
            categoryDescription: string
            aggregate: {
              items: {
                jurisdiction: {
                  address2: string
                }
                type: string
                amount: number
                percentage: number
              }[]
              amount: number
            }
            taxablePriceEach: number
          }
        }
      }[]
    }[]
    aggregate: {
      weight: {
        unit?: string
        value: number
      }
    }
  }
  terms: {
    subtotal: number
    discount: {
      amount: number
    }
    freight: {
      amount: number
      terms: {
        tax: {
          aggregate: {
            items: {
              jurisdiction: {
                address2?: string
                country?: string
              }
              type: string
              amount: number
              percentage: number
            }[]
            amount: number
          }
        }
      }
    }
    pretotal: number
    pv: number
    tax: {
      amount: number
      items?: {
        amount: number
        categoryDescription: string
        description: string
        jurisdiction: {
          country: string
          state?: string
        }
        percentage: number
        type: string
        taxedAmount: number
      }[]
    }
    taxableTotal: number
    timbre: {
      amount: number
      percentage?: null | number
    }
    total: number
    weight: number
    discountDisplay?: {
      amount: number
    }
  }
  additionalNotes: string
  dateCreated: string
  currency: string
  giftReceipt: boolean
  taxedAs?: string
  added_lines: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items: any[] // !TODO: replace with proper type
    aggregate?: {
      weight: {
        unit: string
        value: number
      }
    }
  }
  recurrence?: OrderRecurrenceType
  seller?: {
    href: string
    id: {
      unicity: string
    }
  }
  added_transactions?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items: any[] // !TODO: replace with proper type
  }
  id: {
    unicity: string
    unity?: number
  }
  href: string
  transactions: {
    items: {
      amount: number
      method: string
      type: string
      transactionIndex?: number
      index?: number
      billToAddress?: {
        address1: string
        address2: string
        city: string
        state: string
        zip: string
        country: string
      }

      billToEmail?: string
      billToName?: {
        firstName: string
        lastName: string
      }
      billToPhone?: string
      authorization?: string
      id?: {
        teller: number
      }
      methodDetails?: {
        bankName?: string
        challengeHTML?: string
        alias?: string
        aliasIsAssociatedWithAutoOrder?: boolean
        creditCardExpires?: string
        creditCardNetwork?: string
        creditCardNumberMasked?: string
        payer?: string
        firstGatewayTransactionId?: string
        iouReceipt?: {
          url?: string
          href?: string
          formData?: {
            fields: {
              MerchantCode: string
              PaymentId: string
              RefNo: number
              Amount: string
              Currency: string
              ProdDesc: string
              UserName: string
              UserEmail: string
              UserContact: string | null
              Remark: number | string
              Lang: string
              Signature: string
              ResponseURL: string
              BackendURL: string
              ActionType?: 'BT' // Optional property
            }
            form: {
              action: string
              method: string
              target: string
            }
            autoSubmit: boolean
          }
        }
        hostedPaymentPageUrl?: string
      }
    }[]

    isQuickPay?: boolean
    aggregate: {
      amount: number
    }
  }
  creator: {
    humanName: {
      fullName: null | string
    }
  }
}

export const buildHeaders = ({ locale }: { locale: SupportedLocale }) => ({
  'Accept-Language': locale,
})

export const HydraPostOrder = async ({
  locale,
  order,
  token,
  pathPart = 'orders',
  timeout,
  returnStructuredErrors,
}: Props) => {
  const path = token ? `/customers/me/${pathPart}` : `/${pathPart}`

  const headers = buildHeaders({ locale })
  const withAuth = !!token

  const request: SendRequestType = {
    method: 'POST',
    withAuth,
    token,
    headers,
    data: order,
    url: path,
    timeout,
    returnStructuredErrors,
  }

  return httpTools.sendRequest<HydraPostOrderResponse & HydraErrorType>(request)
}
